import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  buildTimezoneOptions,
  DEFAULT_TIME_FORMAT,
  formatCurrentTime,
  getDefaultTimezone,
  getSlotIdentifier,
  guessTimeFormat,
  normalizeAvailableSlots,
  normalizeTimeFormat,
  slotStartsInPast,
  todayKeyInTimezone,
  toLocalTimeRange,
  WEEKDAYS,
} from './calendar_scheduler_utils';
import Loader from './loader';
import SearchableSelect from './searchable_select';

const dateKey = date => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const monthRange = date => {
  const y = date.getFullYear();
  const m = date.getMonth();
  const fromDate = dateKey(new Date(y, m, 1));
  const toDate = dateKey(new Date(y, m + 1, 0));

  return { key: fromDate.slice(0, 7), fromDate, toDate };
};

const slotsOnDate = (slotsByWeekday, dateObj) => {
  if (!dateObj) return [];
  const dateString = dateKey(dateObj);
  const weekday = WEEKDAYS[dateObj.getDay()];
  return (slotsByWeekday?.[weekday] || []).filter(slot => !slot.date || slot.date === dateString);
};

export function CalendarScheduler({
  pushEventTo,
  value,
  onChange,
  readonly = false,
  target,
  duration_options = [],
  fixed_slots = false,
  all_days_available = false,
  overrides = [],
  available_slots = {},
  time_format = DEFAULT_TIME_FORMAT,
  loadOverrides,
  scheduleId,
}) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const initial = value?.selected_date;
    if (!initial) return new Date();
    const [y, m] = initial.split('-').map(Number);
    return new Date(y, m - 1, 1);
  });
  const [selectedDate, setSelectedDate] = useState(value?.selected_date || null);
  const [selectedTime, setSelectedTime] = useState(value?.selected_time || null);
  const [availableSlots, setAvailableSlots] = useState(() => normalizeAvailableSlots(available_slots));
  const [availableDates, setAvailableDates] = useState(value?.available_dates || []);
  const [selectedSlotAlias, setSelectedSlotAlias] = useState(null);
  const [isBootLoading, setIsBootLoading] = useState(true);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [currentDuration, setCurrentDuration] = useState(
    value?.duration_minutes > 0 ? value.duration_minutes : duration_options?.[0] || 60,
  );
  const [selectedTimezone, setSelectedTimezone] = useState(() => value?.display_timezone || getDefaultTimezone());
  const [overridePages, setOverridePages] = useState({});

  const lastValueRef = useRef();
  const isUpdatingRef = useRef(false);
  const loadedOverrideMonthsRef = useRef(new Set());
  const requestedOverrideMonthsRef = useRef(new Set());
  const timeFormat = normalizeTimeFormat(time_format);
  const [displayTimeFormat, setDisplayTimeFormat] = useState(
    value?.time_format ? normalizeTimeFormat(value.time_format) : guessTimeFormat(selectedTimezone, timeFormat),
  );
  const sourceTimezone = value?.timezone || 'Etc/UTC';
  const timezoneOptions = useMemo(() => {
    const supported = typeof Intl?.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
    const currentTimezone = selectedTimezone || getDefaultTimezone();
    return supported.includes(currentTimezone) ? supported : [currentTimezone, ...supported].filter(Boolean);
  }, [selectedTimezone]);
  const timezoneSelectOptions = useMemo(() => buildTimezoneOptions(timezoneOptions), [timezoneOptions]);
  const todayKey = todayKeyInTimezone(selectedTimezone);

  useEffect(() => {
    setAvailableSlots(normalizeAvailableSlots(available_slots));
  }, [available_slots]);

  useEffect(() => {
    loadedOverrideMonthsRef.current.clear();
    requestedOverrideMonthsRef.current.clear();
    setOverridePages({});
  }, [scheduleId]);

  useEffect(() => {
    if (!loadOverrides || !pushEventTo || !target) return;

    const targetEl = document.getElementById(target);
    if (!targetEl) return;

    const { key, fromDate, toDate } = monthRange(currentMonth);
    if (loadedOverrideMonthsRef.current.has(key) || requestedOverrideMonthsRef.current.has(key)) return;

    requestedOverrideMonthsRef.current.add(key);

    pushEventTo(targetEl, loadOverrides, { from_date: fromDate, to_date: toDate }, reply => {
      requestedOverrideMonthsRef.current.delete(key);

      if (!reply || !Array.isArray(reply.overrides)) return;

      loadedOverrideMonthsRef.current.add(key);
      setOverridePages(pages => ({ ...pages, [key]: reply.overrides }));
    });
  }, [currentMonth, loadOverrides, pushEventTo, scheduleId, target]);

  useEffect(() => {
    if (!value || isUpdatingRef.current) {
      isUpdatingRef.current = false;
      return;
    }

    const normalizedSlotsFromValue = normalizeAvailableSlots(value?.available_slots || available_slots);

    const normalizedSelectedDate = (() => {
      const rawDate = value.selected_date;
      if (!rawDate) return null;
      if (typeof rawDate === 'string') return rawDate;
      if (rawDate?.__type === 'date_v2') {
        return rawDate?.__value?.date || null;
      }
      if (typeof rawDate === 'object' && rawDate.date) return rawDate.date;
      if (typeof rawDate === 'object' && rawDate.value) return rawDate.value;
      return null;
    })();

    const normalizedSelectedTime = typeof value.selected_time === 'string' ? value.selected_time : null;

    const normalizedValue = {
      selected_date: normalizedSelectedDate,
      selected_time: normalizedSelectedTime,
      duration_minutes: value.duration_minutes > 0 ? value.duration_minutes : 60,
      available_slots: normalizedSlotsFromValue,
      available_dates: value.available_dates || [],
      timezone: value.timezone || 'Etc/UTC',
      display_timezone: value.display_timezone || getDefaultTimezone(),
      time_format: normalizeTimeFormat(value.time_format || time_format),
    };

    const slotsForMatch = normalizedValue.selected_date
      ? (() => {
          const [year, month, day] = normalizedValue.selected_date.split('-').map(Number);
          return slotsOnDate(normalizedSlotsFromValue, new Date(year, month - 1, day));
        })()
      : Object.values(normalizedSlotsFromValue).reduce((acc, daySlots) => acc.concat(daySlots), []);

    const matchedSlot = normalizedValue.selected_time
      ? slotsForMatch.find(s => s.time === normalizedValue.selected_time)
      : null;

    const lastValue = lastValueRef.current;
    if (lastValue && JSON.stringify(lastValue) === JSON.stringify(normalizedValue)) return;

    lastValueRef.current = normalizedValue;
    setSelectedDate(prev => normalizedValue.selected_date || prev);
    setSelectedTime(prev => normalizedValue.selected_time || prev);
    setAvailableSlots(normalizedSlotsFromValue);
    setAvailableDates(normalizedValue.available_dates);
    setSelectedSlotAlias(getSlotIdentifier(matchedSlot) || null);
    setCurrentDuration(normalizedValue.duration_minutes);
    setIsLoadingSlots(false);
    setSelectedTimezone(normalizedValue.display_timezone);
    setDisplayTimeFormat(normalizedValue.time_format);
    setIsBootLoading(false);
  }, [value, available_slots, time_format]);

  const mergedOverrides = useMemo(
    () => [...(overrides || []), ...Object.values(overridePages).flat()],
    [overrides, overridePages],
  );

  const goToPreviousMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const goToNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  const getDaysInMonth = date => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0);
    const days = [];
    for (let i = 0; i < start.getDay(); i++) days.push(null);
    for (let i = 1; i <= end.getDate(); i++) days.push(new Date(y, m, i));
    return days;
  };

  const isPastDate = d => {
    if (!d) return false;
    return dateKey(d) < todayKey;
  };

  const slotIsActive = slot => slot && slot.available !== false;

  const hasSlotsForDate = dateObj => slotsOnDate(availableSlots, dateObj).some(slotIsActive);

  const isDateSelected = d => {
    if (!d || !selectedDate) return false;
    return dateKey(d) === selectedDate;
  };

  const isToday = d => d && dateKey(d) === todayKey;

  const getSlotOverridesForDate = dateString => {
    return mergedOverrides.filter(o => o.day === dateString && o.time);
  };

  const getDayOverrideForDate = dateString => {
    return mergedOverrides.find(o => o.day === dateString && !o.time);
  };

  const hasFutureSlotsForDate = dateObj => {
    if (!dateObj) return false;
    const dateString = dateKey(dateObj);
    const daySlots = slotsOnDate(availableSlots, dateObj);
    const slotOverrides = getSlotOverridesForDate(dateString);
    const blockedTimes = new Set(slotOverrides.filter(o => !o.available).map(o => o.time));

    return daySlots
      .filter(slotIsActive)
      .filter(s => !blockedTimes.has(s.time))
      .some(s => !slotStartsInPast(dateString, s.time, sourceTimezone, selectedTimezone));
  };

  const isDateAvailable = d => {
    if (!d) return false;
    const dateString = dateKey(d);
    const dayOverride = getDayOverrideForDate(dateString);
    const slotsAvailable = hasSlotsForDate(d);
    if (!slotsAvailable) return false;
    if (dayOverride && !dayOverride.available) return false;

    const slotOverrides = getSlotOverridesForDate(dateString);
    if (slotOverrides.length > 0) {
      const daySlots = slotsOnDate(availableSlots, d);
      const activeSlots = daySlots.filter(slotIsActive);
      const blockedTimes = new Set(slotOverrides.filter(o => !o.available).map(o => o.time));
      const hasAvailable = activeSlots.some(s => !blockedTimes.has(s.time));
      if (!hasAvailable) return false;
    }

    if (isPastDate(d) || !hasFutureSlotsForDate(d)) return false;
    if (dayOverride) return true;
    if (all_days_available) return true;
    return availableDates.includes(dateString);
  };

  const handleDateClick = dateObj => {
    if (readonly) return;
    const dateString = dateKey(dateObj);

    if (dateString === selectedDate) return;

    isUpdatingRef.current = true;
    setSelectedDate(dateString);
    setSelectedTime(null);
    setSelectedSlotAlias(null);

    const newValue = {
      selected_date: dateString,
      selected_time: null,
      duration_minutes: currentDuration,
      available_slots: availableSlots,
      available_dates: availableDates,
      timezone: sourceTimezone,
      display_timezone: selectedTimezone,
      time_format: displayTimeFormat,
    };

    const targetEl = document.getElementById(target);
    if (targetEl && onChange && pushEventTo) pushEventTo(targetEl, onChange, { value: newValue });
  };

  const handleTimeClick = slot => {
    if (readonly || !slot.available || slotStartsInPast(selectedDate, slot.time, sourceTimezone, selectedTimezone))
      return;
    isUpdatingRef.current = true;

    const dur = slot.duration_minutes > 0 ? slot.duration_minutes : currentDuration;
    setSelectedTime(slot.time);
    setCurrentDuration(dur);
    setSelectedSlotAlias(getSlotIdentifier(slot) || null);

    const newValue = {
      selected_date: selectedDate,
      selected_time: slot.time,
      duration_minutes: dur,
      available_slots: availableSlots,
      available_dates: availableDates,
      timezone: sourceTimezone,
      display_timezone: selectedTimezone,
      time_format: displayTimeFormat,
    };

    const targetEl = document.getElementById(target);
    if (targetEl && onChange && pushEventTo) pushEventTo(targetEl, onChange, { value: newValue });
  };

  const handleDurationChange = val => {
    if (readonly || fixed_slots) return;
    const v = parseInt(val, 10);
    isUpdatingRef.current = true;
    setCurrentDuration(v);
    setSelectedTime(null);
    setSelectedSlotAlias(null);
    setIsLoadingSlots(true);

    const newValue = {
      selected_date: selectedDate,
      selected_time: null,
      duration_minutes: v,
      available_slots: availableSlots,
      available_dates: availableDates,
      timezone: sourceTimezone,
      display_timezone: selectedTimezone,
      time_format: displayTimeFormat,
    };

    const targetEl = document.getElementById(target);
    if (targetEl && onChange && pushEventTo) pushEventTo(targetEl, onChange, { value: newValue });

    setTimeout(() => (isUpdatingRef.current = false), 100);
  };

  const visibleSlots = useMemo(() => {
    if (!selectedDate) return [];
    const [y, m, d] = selectedDate.split('-').map(Number);
    const localDate = new Date(y, m - 1, d);
    const daySlots = slotsOnDate(availableSlots, localDate);
    const slotOverrides = getSlotOverridesForDate(selectedDate);
    const blockedTimes = new Set(slotOverrides.filter(o => !o.available).map(o => o.time));
    return daySlots
      .filter(s => !blockedTimes.has(s.time))
      .filter(s => fixed_slots || typeof s.duration_minutes !== 'number' || s.duration_minutes === currentDuration)
      .filter(s => !slotStartsInPast(selectedDate, s.time, sourceTimezone, selectedTimezone))
      .map(s => ({
        ...s,
        duration_minutes: s.duration_minutes ?? currentDuration,
      }));
  }, [availableSlots, selectedDate, currentDuration, fixed_slots, mergedOverrides, sourceTimezone, selectedTimezone]);

  const hasAliasesForSelectedDate = useMemo(() => visibleSlots.some(s => !!getSlotIdentifier(s)), [visibleSlots]);

  const showDurationPicker = !(fixed_slots || hasAliasesForSelectedDate);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const isSlotMatchingSelectedDate = (slot, sd) => {
    if (!slot || !slot.weekday || !sd) return true;
    const [y, m, d] = sd.split('-').map(Number);
    const local = new Date(y, m - 1, d);
    return slot.weekday.toLowerCase() === WEEKDAYS[local.getDay()];
  };

  const LOADER_MIN_HEIGHT = 420;

  if (isBootLoading) {
    return (
      <div
        className='mx-auto flex w-full max-w-4xl items-center justify-center py-8'
        style={{ minHeight: LOADER_MIN_HEIGHT }}
      >
        <Loader showLabel={false} minHeight={LOADER_MIN_HEIGHT} />
      </div>
    );
  }

  return (
    <div className='mx-auto w-full max-w-4xl py-2'>
      <div className='grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4'>
        <div>
          <h3 className='text-foreground mb-3 text-base font-semibold lg:mb-4 lg:text-lg'>Select a date</h3>
          <div className='mb-2 flex items-center justify-between lg:mb-3'>
            <button
              type='button'
              onClick={goToPreviousMonth}
              disabled={readonly}
              className='rounded-base text-foreground hover:bg-accent p-1.5 disabled:cursor-not-allowed disabled:opacity-50 lg:p-2'
            >
              <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
              </svg>
            </button>
            <h4 className='text-foreground text-sm font-medium lg:text-lg'>
              {currentMonth.toLocaleString('en-US', { month: 'long' })} {currentMonth.getFullYear()}
            </h4>
            <button
              type='button'
              onClick={goToNextMonth}
              disabled={readonly}
              className='rounded-base text-foreground hover:bg-accent p-1.5 disabled:cursor-not-allowed disabled:opacity-50 lg:p-2'
            >
              <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
              </svg>
            </button>
          </div>

          <div className='mb-1 grid grid-cols-7 gap-0.5'>
            {dayNames.map(d => (
              <div key={d} className='text-foreground-soft py-1 text-center text-xs font-medium'>
                {d}
              </div>
            ))}
          </div>

          <div className='grid grid-cols-7 gap-0.5'>
            {getDaysInMonth(currentMonth).map((day, i) => (
              <div
                key={day ? `${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}` : `empty-${i}`}
                className='aspect-square'
              >
                {day ? (
                  (() => {
                    const isDisabled = readonly || isPastDate(day) || !isDateAvailable(day);
                    const isSelectedDay = isDateSelected(day);
                    const todayHighlight = isToday(day) && !isSelectedDay && isDateAvailable(day);

                    return (
                      <button
                        type='button'
                        onClick={() => handleDateClick(day)}
                        disabled={isDisabled}
                        className={`rounded-base h-full w-full border text-xs font-medium transition-colors ${
                          isSelectedDay
                            ? 'border-primary bg-primary text-foreground-primary hover:bg-primary'
                            : isDisabled
                              ? 'border-base bg-input-disabled text-foreground-softest'
                              : 'border-base bg-input text-foreground hover:border-primary'
                        } ${
                          todayHighlight ? 'ring-primary ring-1' : ''
                        } ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                      >
                        {day.getDate()}
                        {isDateAvailable(day) && !isPastDate(day) && (
                          <div className='bg-primary mx-auto mt-0.5 h-1 w-1 rounded-full' />
                        )}
                      </button>
                    );
                  })()
                ) : (
                  <div className='h-full w-full' />
                )}
              </div>
            ))}
          </div>

          <div className='mt-4 space-y-3'>
            <div className='flex items-center gap-2'>
              <svg className='text-foreground-softer h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' d='M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z' />
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z'
                />
              </svg>
              <span className='text-foreground-soft text-xs font-medium lg:text-sm'>Time zone</span>
            </div>
            <SearchableSelect
              value={selectedTimezone}
              onChange={tz => {
                setSelectedTimezone(tz);
                isUpdatingRef.current = true;
                const newValue = {
                  selected_date: selectedDate,
                  selected_time: selectedTime,
                  duration_minutes: currentDuration,
                  available_slots: availableSlots,
                  available_dates: availableDates,
                  timezone: sourceTimezone,
                  display_timezone: tz,
                  time_format: displayTimeFormat,
                };
                const targetEl = document.getElementById(target);
                if (targetEl && onChange && pushEventTo) pushEventTo(targetEl, onChange, { value: newValue });
              }}
              options={timezoneSelectOptions}
              disabled={readonly}
              readOnly={readonly}
              className='w-full'
              searchPlaceholder='Search...'
              variant='field'
              renderOptionExtra={tz => formatCurrentTime(tz, displayTimeFormat)}
            />
          </div>
        </div>

        <div>
          <h3 className='text-foreground mb-2 text-base font-semibold lg:mb-3 lg:text-lg'>Select a time</h3>

          {showDurationPicker && (
            <div className='flex flex-col gap-1.5 lg:gap-2'>
              <span className='text-foreground-soft text-xs font-medium lg:text-sm'>Duration:</span>
              <div className='flex flex-wrap gap-1 lg:gap-1.5'>
                {(duration_options.length > 0 ? duration_options : [15, 30, 45, 60, 90]).map(opt => (
                  <button
                    key={opt}
                    type='button'
                    onClick={() => handleDurationChange(opt)}
                    disabled={readonly}
                    aria-pressed={currentDuration === opt}
                    className={`rounded-base flex h-7 items-center justify-center border px-2 py-1 text-xs font-medium transition-all lg:h-8 lg:px-3 lg:py-1.5 ${
                      currentDuration === opt
                        ? 'border-primary bg-primary text-foreground-primary shadow-sm'
                        : 'border-base bg-input text-foreground hover:border-primary'
                    } ${readonly ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    {opt} min
                  </button>
                ))}
              </div>
            </div>
          )}

          {!selectedDate ? (
            <div className='text-foreground-soft py-8 text-center text-sm'>Select a date to see available times</div>
          ) : isLoadingSlots ? (
            <Loader showLabel={false} minHeight={200} />
          ) : (
            <div className='max-h-64 overflow-y-auto pl-px pr-1 lg:max-h-80'>
              <div className='space-y-2 pb-3 lg:pb-4'>
                {visibleSlots.map((slot, i) => {
                  const slotIdentifier = getSlotIdentifier(slot);
                  const isSelected =
                    selectedTime === slot.time &&
                    (!slotIdentifier || selectedSlotAlias === slotIdentifier) &&
                    isSlotMatchingSelectedDate(slot, selectedDate);
                  const { startText: displayStart, endText: displayEnd } = toLocalTimeRange(
                    selectedDate,
                    slot.time,
                    slot.duration_minutes ?? currentDuration,
                    displayTimeFormat,
                    sourceTimezone,
                    selectedTimezone,
                  );
                  return (
                    <button
                      key={`${slot.time}-${slotIdentifier ?? 'noalias'}-${i}`}
                      type='button'
                      onClick={() => handleTimeClick(slot)}
                      disabled={readonly || !slot.available}
                      className={`rounded-base w-full border p-4 text-left transition-all ${
                        isSelected
                          ? 'border-primary bg-primary text-foreground-primary'
                          : slot.available
                            ? 'border-base bg-input text-foreground hover:border-primary'
                            : 'border-base bg-input-disabled text-foreground-softest cursor-not-allowed'
                      } ${readonly ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      <div className='flex justify-center'>
                        <div className='text-xs font-semibold lg:text-sm'>
                          {displayStart} – {displayEnd}
                        </div>
                        {!slot.available && (
                          <span className='rounded-base bg-input-disabled text-foreground-soft px-1.5 py-0.5 text-xs'>
                            Unavailable
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
